import type { ClientCampaignCollaboration, ClientCollaborationProduct } from '@/api/beatlyApi';
import type { CollaborationTrackerRow, Collaborator, CollaboratorBriefLink, Post, PostCompliance } from '@/components/ContentReviewSection';
import { inferPlatformFromContentHints, type SocialViewPlatform } from '@/utils/publishedSocialEmbedUrl';

type ContentPlatform = NonNullable<Post['platform']>;
type ComplianceFlag = boolean | null;

/** Stable numeric id from collaboration_id (for expand state / React keys). */
function collaborationIdToNumericId(collaborationId: string): number {
  let h = 0;
  for (let i = 0; i < collaborationId.length; i++) {
    h = (h * 31 + collaborationId.charCodeAt(i)) | 0;
  }
  const n = Math.abs(h);
  return n || 1;
}

function formatFollowers(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatCollabDate(iso: string | undefined | null): string {
  if (!iso || typeof iso !== 'string') return '–';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '–';
  }
}

function apiPlatformToUi(platform?: string): ContentPlatform {
  const p = (platform ?? 'INSTAGRAM').toUpperCase();
  if (p === 'TIKTOK') return 'TikTok';
  if (p === 'YOUTUBE') return 'YouTube';
  return 'Instagram';
}

function isLikelyVideoUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const lower = url.trim().toLowerCase();
  const [base] = lower.split('?');
  if (/\.(mp4|mov|webm|m4v|m3u8)(\?|#|$)/i.test(base)) return true;
  if (lower.includes('/video/') || lower.includes('video.mp4')) return true;
  return false;
}

/** Extra keys on collaboration products (API may extend payload beyond typed fields). */
function productRaw(p: ClientCollaborationProduct): Record<string, unknown> {
  return p as Record<string, unknown>;
}

function readFiniteNumber(raw: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return undefined;
}

function readOptionalString(raw: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return undefined;
}

function inferPostType(product: ClientCollaborationProduct): Post['type'] {
  const raw = productRaw(product);
  const camelName = readOptionalString(raw, 'productName') ?? '';
  const camelType = readOptionalString(raw, 'productType') ?? '';
  const blob =
    `${product.product_name ?? ''} ${product.product_type ?? ''} ${camelName} ${camelType}`.toUpperCase();
  if (blob.includes('STORY')) return 'Story';
  if (blob.includes('CAROUSEL') || blob.includes('CARROUSEL')) return 'Carousel';
  return 'Reel';
}

function readStringArray(raw: Record<string, unknown>, ...keys: string[]): string[] {
  for (const key of keys) {
    const v = raw[key];
    if (!Array.isArray(v)) continue;
    const out = v
      .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
      .map((s) => s.trim());
    if (out.length > 0) return out;
  }
  return [];
}

const EMPTY_COMPLIANCE: PostCompliance = {
  brandTagged: null,
  hashtagUsed: null,
  productVisible: null,
  adDisclosure: null,
  linkIncluded: null,
};

function parseComplianceFromPayload(raw: Record<string, unknown>): { fromApi: boolean; compliance: PostCompliance } {
  const c = raw.compliance;
  if (!c || typeof c !== 'object' || Array.isArray(c)) {
    return { fromApi: false, compliance: { ...EMPTY_COMPLIANCE } };
  }
  const o = c as Record<string, unknown>;
  const touched = (keys: string[]) => keys.some((k) => o[k] !== undefined && o[k] !== null);
  const hasFlags = touched([
    'brandTagged',
    'brand_tagged',
    'hashtagUsed',
    'hashtag_used',
    'productVisible',
    'product_visible',
    'adDisclosure',
    'ad_disclosure',
    'linkIncluded',
    'link_included',
  ]);
  if (!hasFlags) {
    return { fromApi: false, compliance: { ...EMPTY_COMPLIANCE } };
  }
  const flag = (a: string, b: string): ComplianceFlag => {
    const va = o[a];
    const vb = o[b];
    if (va === true || vb === true) return true;
    if (va === false || vb === false) return false;
    return null;
  };
  return {
    fromApi: true,
    compliance: {
      brandTagged: flag('brandTagged', 'brand_tagged'),
      hashtagUsed: flag('hashtagUsed', 'hashtag_used'),
      productVisible: flag('productVisible', 'product_visible'),
      adDisclosure: flag('adDisclosure', 'ad_disclosure'),
      linkIncluded: flag('linkIncluded', 'link_included'),
    },
  };
}

function payloadHasMetricFields(raw: Record<string, unknown>): boolean {
  const keys = [
    'likes',
    'like_count',
    'comments',
    'comment_count',
    'reach',
    'impressions',
    'impression_count',
    'saves',
    'save_count',
    'shares',
    'share_count',
    'engagement_rate',
    'engagementRate',
    'link_clicks',
    'link_clicks_count',
    'views',
    'view_count',
    'plays',
    'play_count',
  ];
  return keys.some((k) => {
    const v = raw[k];
    if (v === null || v === undefined) return false;
    if (typeof v === 'number' && Number.isFinite(v)) return true;
    if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return true;
    return false;
  });
}

function normalizeEngagementRate(raw: Record<string, unknown>): number {
  const v = readFiniteNumber(raw, 'engagement_rate', 'engagementRate');
  if (v === undefined || !Number.isFinite(v)) return 0;
  return v <= 1 ? v * 100 : v;
}

function looksLikeHttpUrl(s: string): boolean {
  const t = s.trim();
  if (t.length < 12 || t.length > 4000) return false;
  if (/^https?:\/\//i.test(t)) return true;
  return t.startsWith('//') && /^\/\/[\w.-]+\//.test(t);
}

function looksLikeRasterImageUrl(s: string): boolean {
  if (!looksLikeHttpUrl(s)) return false;
  const lower = s.trim().toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|avif|svg)(\?|#|$)/i.test(lower)) return true;
  if (lower.includes('cdninstagram.com') || lower.includes('fbcdn.net')) return true;
  if (lower.includes('thumbnail') || lower.includes('/image')) return true;
  if (lower.includes('cloudinary.com') && lower.includes('/image/upload')) return true;
  return false;
}

/**
 * Only *obvious* mugshot / profile URLs. IG/TikTok often use small dimensions or t51.* paths for real post
 * thumbnails too — stricter rules were stripping all previews (empty pool).
 */
function isLikelyCreatorAvatarOrMugshotUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const lower = url.trim().toLowerCase();
  if (lower.includes('mugshot')) return true;
  if (lower.includes('profile_pic') || lower.includes('profile-pic')) return true;
  if ((lower.includes('tiktok') || lower.includes('tiktokcdn')) && lower.includes('avatar')) return true;
  return false;
}

/** Do not recurse into these keys when hunting post media (avoids influencer mugshots, etc.). */
function shouldSkipMediaSubtreeKey(key: string): boolean {
  const k = key.toLowerCase();
  const skip = new Set([
    'influencer',
    'creator',
    'user',
    'author',
    'owner',
    'account',
    'profile',
    'publisher',
    'brand',
    'client',
    'invited_by',
    'invitedby',
    'team',
    'assignee',
  ]);
  return skip.has(k);
}

function collectRasterImageUrls(value: unknown, depth: number, out: string[]): void {
  if (depth > 8 || value == null || out.length > 40) return;
  if (typeof value === 'string') {
    const t = value.trim();
    if (looksLikeRasterImageUrl(t)) out.push(t);
    return;
  }
  if (Array.isArray(value)) {
    for (const x of value) collectRasterImageUrls(x, depth + 1, out);
    return;
  }
  if (typeof value === 'object') {
    for (const k of Object.keys(value as object)) {
      if (shouldSkipMediaSubtreeKey(k)) continue;
      collectRasterImageUrls((value as Record<string, unknown>)[k], depth + 1, out);
    }
  }
}

function looksLikeMediaCdnUrl(s: string): boolean {
  const lower = s.trim().toLowerCase();
  return (
    lower.includes('cdninstagram.com') ||
    lower.includes('fbcdn.net') ||
    lower.includes('tiktokcdn.com') ||
    lower.includes('tiktokv.com') ||
    lower.includes('ibyteimg.com') ||
    lower.includes('twimg.com') ||
    lower.includes('ytimg.com') ||
    lower.includes('ggpht.com') ||
    lower.includes('pinimg.com') ||
    lower.includes('cloudfront.net') ||
    (lower.includes('cloudinary.com') && lower.includes('/image/upload'))
  );
}

/** True for post/watch permalinks that cannot be used as <img src> / video src. Exported for merge + UI. */
export function isProbablyNonMediaPageUrl(url: string): boolean {
  const s = url.toLowerCase();
  if (/instagram\.com\/(p|reel|tv|stories)\b/i.test(s)) return true;
  if (s.includes('tiktok.com/') && !looksLikeMediaCdnUrl(s)) return true;
  if (s.includes('youtube.com/watch') || s.includes('youtu.be/')) return true;
  return false;
}

/** Normalize API quirks (e.g. protocol-relative CDN URLs). */
export function normalizeCollaborationThumbUrl(url: string): string {
  const t = (url ?? '').trim();
  if (t.startsWith('//')) return `https:${t}`;
  return t;
}

/**
 * True when the URL can be used as <img src> / <video src> in the collaborations grid.
 * Treats known CDNs and file extensions as displayable even if the string slipped past preview_url heuristics.
 */
export function isDisplayableCollaborationThumbUrl(url: string): boolean {
  const t = normalizeCollaborationThumbUrl(url);
  if (!t || t.startsWith('data:image/svg+xml')) return false;
  if (!/^https?:\/\//i.test(t)) return false;
  if (looksLikeMediaCdnUrl(t)) return true;
  const base = t.split('?')[0].toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|avif|mp4|webm|mov|m4v|m3u8)(\?|#|$)/i.test(base)) return true;
  if (isProbablyNonMediaPageUrl(t)) return false;
  if (isIgNonCdnHostname(t)) return false;
  return true;
}

function isIgNonCdnHostname(url: string): boolean {
  try {
    const u = new URL(url);
    const h = u.hostname.toLowerCase();
    if (!h.endsWith('instagram.com')) return false;
    return !h.includes('cdninstagram') && !h.startsWith('scontent');
  } catch {
    return false;
  }
}

/** Deep-scan product JSON for any direct image/video CDN URL the API might nest under custom keys. */
function collectDeepMediaUrls(value: unknown, depth: number, out: string[]): void {
  if (depth > 10 || value == null || out.length > 80) return;
  if (typeof value === 'string') {
    const t = value.trim();
    if (!looksLikeHttpUrl(t) || isProbablyNonMediaPageUrl(t) || isIgNonCdnHostname(t)) return;
    if (looksLikeRasterImageUrl(t) || isLikelyVideoUrl(t) || looksLikeMediaCdnUrl(t)) {
      out.push(t);
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const x of value) collectDeepMediaUrls(x, depth + 1, out);
    return;
  }
  if (typeof value === 'object') {
    for (const k of Object.keys(value as object)) {
      if (shouldSkipMediaSubtreeKey(k)) continue;
      collectDeepMediaUrls((value as Record<string, unknown>)[k], depth + 1, out);
    }
  }
}

function readNestedPreview(o: Record<string, unknown> | undefined): string[] {
  if (!o) return [];
  const found: string[] = [];
  const u = readOptionalString(
    o,
    'preview_url',
    'thumbnail',
    'thumbnail_url',
    'thumbnailUrl',
    'post_thumbnail_url',
    'postThumbnailUrl',
    'cover_url',
    'coverUrl',
    'cover_image',
    'coverImage',
    'image_url',
    'imageUrl',
    'picture_url',
    'pictureUrl',
    'poster_url',
    'posterUrl',
    'video_thumbnail_url',
    'videoThumbnailUrl',
    'screenshot_url',
    'screenshotUrl',
    'asset_url',
    'assetUrl',
    'creative_url',
    'creativeUrl',
    'file_url',
    'fileUrl',
    'hero_image_url',
    'heroImageUrl',
    'display_url',
    'low_resolution_url',
    'standard_resolution_url',
    'high_resolution_url',
    'media_url',
    'og_image',
    'og_image_url',
    'ogImageUrl',
    'visual_url',
    'visualUrl',
    'published_image_url',
    'publishedImageUrl',
    'instagram_media_url',
    'instagramMediaUrl',
    'url',
  );
  if (u) found.push(u);
  const nestKeys = [
    'draft',
    'content',
    'creative',
    'instagram',
    'published',
    'published_post',
    'published_content',
    'published_media',
    'publication',
    'submission',
    'deliverable',
    'deliverables',
    'metrics',
    'insights',
    'post',
    'ig_post',
    'tiktok_post',
    'social_post',
    'final_preview',
    'last_version',
    'latest_version',
    'version',
    'media',
    'first_frame',
    'carousel',
    'media_items',
    'medias',
    'assets',
    'image',
  ];
  for (const k of nestKeys) {
    if (shouldSkipMediaSubtreeKey(k)) continue;
    const v = o[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const inner = readNestedPreview(v as Record<string, unknown>);
      found.push(...inner);
    }
  }

  /** Carousel / multi-slide / multi-asset: recurse each item. */
  const arrayNestKeys = ['carousel', 'media', 'children', 'items', 'slides', 'carousel_items', 'versions'];
  for (const k of arrayNestKeys) {
    if (shouldSkipMediaSubtreeKey(k)) continue;
    const v = o[k];
    if (!Array.isArray(v)) continue;
    for (const item of v) {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        found.push(...readNestedPreview(item as Record<string, unknown>));
      }
    }
  }
  return found;
}

function resolveProductPreviewUrl(
  product: ClientCollaborationProduct,
  collaborationMediaFallbackUrls?: string[],
): string {
  const raw = productRaw(product);
  const candidates: string[] = [];
  const prev = typeof product.preview_url === 'string' ? product.preview_url.trim() : '';
  if (prev && looksLikeHttpUrl(prev)) candidates.push(prev);
  const camelPrev = readOptionalString(raw, 'previewUrl', 'thumbnailUrl', 'imageUrl');
  if (camelPrev && looksLikeHttpUrl(camelPrev)) candidates.push(camelPrev);

  const target = typeof product.target_url === 'string' ? product.target_url.trim() : '';
  if (target && looksLikeHttpUrl(target) && (looksLikeRasterImageUrl(target) || isLikelyVideoUrl(target))) {
    candidates.push(target);
  }

  for (const u of readNestedPreview(raw)) {
    const t = u.trim();
    if (t && looksLikeHttpUrl(t)) candidates.push(t);
  }

  const fromDeep: string[] = [];
  collectRasterImageUrls(raw, 0, fromDeep);
  candidates.push(...fromDeep);

  const deepMedia: string[] = [];
  collectDeepMediaUrls(raw, 0, deepMedia);
  candidates.push(...deepMedia);

  for (const u of collaborationMediaFallbackUrls ?? []) {
    const t = (u ?? '').trim();
    if (t && looksLikeHttpUrl(t)) candidates.push(t);
  }

  const uniq: string[] = [];
  const seen = new Set<string>();
  for (const c of candidates) {
    const t = c.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    uniq.push(t);
  }

  const notPage = (u: string) => !isProbablyNonMediaPageUrl(u) && !isIgNonCdnHostname(u);

  // Prefer URLs that are not obvious mugshots; then any direct media URL; then any http candidate.
  let pool = uniq.filter((u) => notPage(u) && !isLikelyCreatorAvatarOrMugshotUrl(u));
  if (pool.length === 0) {
    pool = uniq.filter(notPage);
  }
  if (pool.length === 0) {
    pool = uniq.filter((u) => looksLikeHttpUrl(u));
  }

  if (pool.length === 0) {
    return '';
  }

  const usable = (u: string) =>
    looksLikeHttpUrl(u) && !isProbablyNonMediaPageUrl(u) && !isIgNonCdnHostname(u);

  for (const u of pool) {
    if (looksLikeRasterImageUrl(u) && usable(u)) return u;
  }
  for (const u of pool) {
    if (isLikelyVideoUrl(u) && usable(u)) return u;
  }
  for (const u of pool) {
    if (looksLikeMediaCdnUrl(u) && usable(u)) return u;
  }
  for (const u of pool) {
    if (usable(u)) return u;
  }
  return '';
}

function hintToContentPlatform(hint: SocialViewPlatform): ContentPlatform {
  if (hint === 'tiktok') return 'TikTok';
  if (hint === 'youtube') return 'YouTube';
  return 'Instagram';
}

function productToPost(
  product: ClientCollaborationProduct,
  platform: ContentPlatform,
  collaborationMediaFallbackUrls?: string[],
): Post {
  const raw = productRaw(product);
  const { fromApi: complianceFromApi, compliance } = parseComplianceFromPayload(raw);

  const thumb = normalizeCollaborationThumbUrl(resolveProductPreviewUrl(product, collaborationMediaFallbackUrls));
  const vid = isLikelyVideoUrl(thumb);

  const nestedMetrics =
    raw.metrics && typeof raw.metrics === 'object' && !Array.isArray(raw.metrics)
      ? (raw.metrics as Record<string, unknown>)
      : undefined;

  const nestedHasNumericMetric = (nm: Record<string, unknown>) =>
    ['views', 'likes', 'shares', 'comments', 'engagement'].some(
      (k) => readFiniteNumber(nm, k) !== undefined,
    );

  const fromNested = Boolean(nestedMetrics && nestedHasNumericMetric(nestedMetrics));
  const fromTrackerClicks = readFiniteNumber(raw, 'tracker_clicks') !== undefined;
  const metricsFromApi =
    raw.metrics_from_api === true || fromNested || fromTrackerClicks || payloadHasMetricFields(raw);

  const caption =
    readOptionalString(raw, 'caption', 'description', 'text', 'title') ??
    (typeof product.product_name === 'string' && product.product_name.trim()
      ? product.product_name.trim()
      : readOptionalString(raw, 'productName') ??
        (typeof product.product_type === 'string' && product.product_type.trim()
          ? product.product_type.trim()
          : readOptionalString(raw, 'productType') ?? ''));

  const productPlatformRaw = readOptionalString(
    raw,
    'platform',
    'social_platform',
    'channel',
    'network',
    'social_network',
    'publishing_platform',
  );
  let effectivePlatform = platform;
  if (productPlatformRaw) {
    effectivePlatform = apiPlatformToUi(productPlatformRaw);
  } else {
    const hint = inferPlatformFromContentHints([
      caption,
      typeof product.product_name === 'string' ? product.product_name : undefined,
      typeof product.product_type === 'string' ? product.product_type : undefined,
      readOptionalString(raw, 'productName'),
      readOptionalString(raw, 'productType'),
    ]);
    if (hint) effectivePlatform = hintToContentPlatform(hint);
  }

  let likes = readFiniteNumber(raw, 'likes', 'like_count') ?? 0;
  let comments = readFiniteNumber(raw, 'comments', 'comment_count') ?? 0;
  const reach = readFiniteNumber(raw, 'reach', 'accounts_reached') ?? 0;
  let impressions = readFiniteNumber(raw, 'impressions', 'impression_count') ?? 0;
  const saves = readFiniteNumber(raw, 'saves', 'save_count') ?? 0;
  let shares = readFiniteNumber(raw, 'shares', 'share_count') ?? 0;
  let linkClicks = readFiniteNumber(raw, 'link_clicks', 'link_clicks_count') ?? 0;
  let views = readFiniteNumber(raw, 'views', 'view_count', 'plays', 'play_count');
  const engagementRate = normalizeEngagementRate(raw);

  if (nestedMetrics) {
    const n = (k: string) => readFiniteNumber(nestedMetrics, k);
    const lv = n('likes');
    if (lv !== undefined) likes = lv;
    const cv = n('comments');
    if (cv !== undefined) comments = cv;
    const vv = n('views');
    if (vv !== undefined) {
      impressions = vv;
      views = vv;
    }
    const sv = n('shares');
    if (sv !== undefined) shares = sv;
  }
  const tlc = readFiniteNumber(raw, 'tracker_clicks');
  if (tlc !== undefined) linkClicks = tlc;

  const productStatusRaw =
    (typeof product.status === 'string' && product.status.trim()
      ? product.status.trim()
      : readOptionalString(raw, 'product_status', 'productStatus')) ?? '';

  const hashtags = readStringArray(raw, 'hashtags', 'hashtag_list', 'hashtagList');
  const brandTags = readStringArray(raw, 'brand_tags', 'brandTags', 'mentions', 'brand_mentions');

  const videoLength =
    readOptionalString(raw, 'video_length', 'videoLength', 'duration', 'duration_label') ?? undefined;

  const post: Post = {
    type: inferPostType(product),
    thumbnail: thumb,
    caption,
    has_metrics_section: raw.has_metrics !== false,
    metrics_from_api: metricsFromApi,
    compliance_from_api: complianceFromApi,
    likes,
    comments,
    date: formatCollabDate(
      readOptionalString(raw, 'date_publish', 'datePublish') ??
        readOptionalString(raw, 'date_draft', 'dateDraft')
    ),
    isVideo: vid,
    reach,
    impressions,
    saves,
    shares,
    engagementRate,
    linkClicks,
    hashtags,
    brandTags,
    compliance,
    platform: effectivePlatform,
  };

  const pid =
    (typeof product.product_id === 'string' && product.product_id.trim()
      ? product.product_id.trim()
      : undefined) ?? readOptionalString(raw, 'productId', 'beat_product_id', 'beatProductId');
  if (pid) post.product_id = pid;

  if (productStatusRaw) post.product_status = productStatusRaw.toUpperCase();

  const productTypeRaw =
    typeof product.product_type === 'string' && product.product_type.trim()
      ? product.product_type.trim()
      : readOptionalString(raw, 'productType');
  if (productTypeRaw) post.product_type = productTypeRaw;

  if (views !== undefined) post.views = views;
  if (videoLength !== undefined) post.videoLength = videoLength;

  if (nestedMetrics) {
    const et = readFiniteNumber(nestedMetrics, 'engagement');
    if (et !== undefined) post.engagement_total = et;
  }

  let permalink =
    (typeof product.permalink === 'string' && product.permalink.trim()
      ? product.permalink.trim()
      : undefined) ??
    readOptionalString(raw, 'permalink', 'post_permalink', 'public_url', 'postUrl', 'post_url');
  const targetForLink = typeof product.target_url === 'string' ? product.target_url.trim() : '';
  if (
    !permalink &&
    targetForLink &&
    looksLikeHttpUrl(targetForLink) &&
    isProbablyNonMediaPageUrl(targetForLink)
  ) {
    permalink = targetForLink;
  }
  if (permalink) post.permalink = permalink;

  const sparkId =
    readOptionalString(raw, 'spark_id', 'sparkId') ??
    (typeof product.spark_id === 'string' && product.spark_id.trim() ? product.spark_id.trim() : undefined);
  if (sparkId) post.spark_id = sparkId;

  const instagramAdId =
    readOptionalString(raw, 'instagram_ad_id', 'instagramAdId') ??
    (typeof product.instagram_ad_id === 'string' && product.instagram_ad_id.trim()
      ? product.instagram_ad_id.trim()
      : undefined);
  if (instagramAdId) post.instagram_ad_id = instagramAdId;

  const avgWatchTime = readOptionalString(raw, 'avg_watch_time', 'avgWatchTime');
  if (avgWatchTime !== undefined) post.avgWatchTime = avgWatchTime;
  const completionRate = readFiniteNumber(raw, 'completion_rate', 'completionRate');
  if (completionRate !== undefined) post.completionRate = completionRate;
  const retention3s = readFiniteNumber(raw, 'retention_3s', 'retention3s');
  if (retention3s !== undefined) post.retention3s = retention3s;
  const storyFrames = readFiniteNumber(raw, 'story_frames', 'storyFrames');
  if (storyFrames !== undefined) post.storyFrames = Math.round(storyFrames);
  const tapsForward = readFiniteNumber(raw, 'taps_forward', 'tapsForward');
  if (tapsForward !== undefined) post.tapsForward = tapsForward;
  const tapsBack = readFiniteNumber(raw, 'taps_back', 'tapsBack');
  if (tapsBack !== undefined) post.tapsBack = tapsBack;
  const exits = readFiniteNumber(raw, 'exits', 'story_exits');
  if (exits !== undefined) post.exits = exits;

  return post;
}

/**
 * Published deliverables are usually on `collaboration.products`, but the API may nest them under beats
 * or use `deliverables` / `publications` on the collaboration or each beat.
 * `products` may be an array **or** a string-keyed record (id → product).
 */
function coalesceIdish(v: unknown): string {
  if (typeof v === 'string' && v.trim()) return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return '';
}

function productDedupeKey(p: ClientCollaborationProduct): string {
  const raw = p as Record<string, unknown>;
  const a = coalesceIdish(p.product_id) || coalesceIdish(raw.productId);
  const b = raw.id;
  const s =
    a ||
    (typeof b === 'string'
      ? b.trim()
      : typeof b === 'number' && Number.isFinite(b)
        ? String(b)
        : '');
  return s;
}

/** Fallback key when API omits product ids. */
function productFingerprint(p: ClientCollaborationProduct): string {
  const raw = productRaw(p);
  return [
    readOptionalString(raw, 'preview_url', 'previewUrl'),
    readOptionalString(raw, 'thumbnail_url', 'thumbnailUrl'),
    readOptionalString(raw, 'target_url', 'targetUrl'),
    readOptionalString(raw, 'date_publish', 'datePublish'),
    readOptionalString(raw, 'date_draft', 'dateDraft'),
    readOptionalString(raw, 'product_name', 'productName'),
    readOptionalString(raw, 'product_type', 'productType'),
  ].join('\x1f');
}

function looksLikeProductObject(o: Record<string, unknown>): boolean {
  if (Object.keys(o).length === 0) return false;

  const hasInfSignal = o.influencer_id != null || o.influencerId != null;
  const hasProductSignal =
    o.product_id != null ||
    o.productId != null ||
    typeof o.product_type === 'string' ||
    typeof o.productType === 'string' ||
    typeof o.product_name === 'string' ||
    typeof o.productName === 'string' ||
    typeof o.preview_url === 'string' ||
    typeof o.previewUrl === 'string' ||
    readOptionalString(o, 'thumbnail_url', 'thumbnailUrl', 'imageUrl') != null ||
    o.date_publish != null ||
    o.date_draft != null ||
    o.datePublish != null ||
    o.dateDraft != null;

  if (hasInfSignal && !hasProductSignal) {
    return false;
  }

  if (
    o.collaboration_id != null &&
    o.product_id == null &&
    o.productId == null &&
    !hasProductSignal
  ) {
    return false;
  }

  return hasProductSignal;
}

function collectDeepProductLikeObjects(
  value: unknown,
  depth: number,
  seenFp: Set<string>,
  out: ClientCollaborationProduct[]
): void {
  if (depth > 14 || value == null) return;
  if (Array.isArray(value)) {
    for (const x of value) collectDeepProductLikeObjects(x, depth + 1, seenFp, out);
    return;
  }
  if (typeof value !== 'object') return;
  const o = value as Record<string, unknown>;
  if (looksLikeProductObject(o)) {
    const p = o as ClientCollaborationProduct;
    const id = productDedupeKey(p);
    const fp = id ? `id:${id}` : productFingerprint(p) || `anon:${depth}:${out.length}`;
    if (seenFp.has(fp)) return;
    seenFp.add(fp);
    out.push(p);
    return;
  }
  for (const k of Object.keys(o)) {
    if (shouldSkipMediaSubtreeKey(k)) continue;
    collectDeepProductLikeObjects(o[k], depth + 1, seenFp, out);
  }
}

function mergeWithDeepScannedProducts(
  collab: ClientCampaignCollaboration,
  primary: ClientCollaborationProduct[]
): ClientCollaborationProduct[] {
  const seen = new Set<string>();
  const out: ClientCollaborationProduct[] = [];
  const add = (p: ClientCollaborationProduct) => {
    const id = productDedupeKey(p);
    const fp = id ? `id:${id}` : productFingerprint(p) || JSON.stringify(p);
    if (seen.has(fp)) return;
    seen.add(fp);
    out.push(p);
  };
  for (const p of primary) add(p);
  const deep: ClientCollaborationProduct[] = [];
  collectDeepProductLikeObjects(collab, 0, new Set(), deep);
  for (const p of deep) add(p);
  return out;
}

/** API may return a list or a map of products / deliverables. */
function appendProductishListOrRecord(value: unknown, add: (item: unknown) => void): void {
  if (value == null) return;
  if (Array.isArray(value)) {
    value.forEach(add);
    return;
  }
  if (typeof value === 'object') {
    for (const v of Object.values(value as Record<string, unknown>)) {
      add(v);
    }
  }
}

function extractCollaborationProducts(collab: ClientCampaignCollaboration): ClientCollaborationProduct[] {
  const out: ClientCollaborationProduct[] = [];
  const seenIds = new Set<string>();

  const add = (item: unknown) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return;
    const p = item as ClientCollaborationProduct;
    const key = productDedupeKey(p);
    if (key) {
      if (seenIds.has(key)) return;
      seenIds.add(key);
    }
    out.push(p);
  };

  appendProductishListOrRecord(collab.products, add);

  const root = collab as Record<string, unknown>;
  for (const key of ['deliverables', 'publications', 'contents', 'posts', 'deliverable_list', 'line_items']) {
    appendProductishListOrRecord(root[key], add);
  }

  const inf = collab.influencer as Record<string, unknown> | undefined;
  if (inf) {
    appendProductishListOrRecord(inf.products, add);
    appendProductishListOrRecord(inf.deliverables, add);
    appendProductishListOrRecord(inf.publications, add);
  }

  if (root.product && typeof root.product === 'object' && !Array.isArray(root.product)) {
    add(root.product);
  }

  const beats = Array.isArray(collab.beats) ? collab.beats : [];
  for (const b of beats) {
    if (!b || typeof b !== 'object' || Array.isArray(b)) continue;
    const br = b as Record<string, unknown>;
    for (const key of ['products', 'deliverables', 'publications', 'contents', 'posts', 'items']) {
      appendProductishListOrRecord(br[key], add);
    }
    if (br.product && typeof br.product === 'object' && !Array.isArray(br.product)) {
      add(br.product);
    }
  }

  return out;
}

/**
 * Prefer collaboration-level `trackers`; if empty, derive unique rows from product tracker fields (api-v2 parity).
 */
function parseCollaborationTrackers(
  c: ClientCampaignCollaboration,
  products: ClientCollaborationProduct[],
): CollaborationTrackerRow[] {
  const rows: CollaborationTrackerRow[] = [];
  const seen = new Set<string>();

  const pushRow = (
    targetUrl?: string | null,
    trackerUrl?: string | null,
    note?: string | null,
  ) => {
    const tu = typeof trackerUrl === 'string' ? trackerUrl.trim() : '';
    const ta = typeof targetUrl === 'string' ? targetUrl.trim() : '';
    if (!tu && !ta) return;
    const key = `${tu}\n${ta}`;
    if (seen.has(key)) return;
    seen.add(key);
    const noteTrim =
      typeof note === 'string' && note.trim().length > 0 ? note.trim() : null;
    rows.push({
      tracker_url: tu || ta,
      target_url: ta || tu,
      tracker_note: noteTrim,
    });
  };

  const rawTrackers = c.trackers;
  if (Array.isArray(rawTrackers)) {
    for (const item of rawTrackers) {
      if (!item || typeof item !== 'object') continue;
      const o = item as Record<string, unknown>;
      pushRow(
        readOptionalString(o, 'target_url', 'targetUrl'),
        readOptionalString(o, 'tracker_url', 'trackerUrl'),
        readOptionalString(o, 'tracker_note', 'trackerNote') ?? null,
      );
    }
  }

  if (rows.length === 0) {
    for (const p of products) {
      const raw = productRaw(p);
      pushRow(
        readOptionalString(raw, 'target_url', 'targetUrl'),
        readOptionalString(raw, 'tracker_url', 'trackerUrl'),
        readOptionalString(raw, 'tracker_note', 'trackerNote') ?? null,
      );
    }
  }

  return rows;
}

function mapRowStatus(
  collab: ClientCampaignCollaboration,
  products: ClientCollaborationProduct[]
): Collaborator['status'] {
  const st = (collab.status ?? '').toUpperCase();
  const productStatuses = products.map((p) => String(p.status ?? '').toUpperCase());
  if (productStatuses.some((s) => s === 'PUBLISHED' || s === 'MEASURED')) return 'Published';
  if (st === 'DONE') return 'Published';
  if (st === 'ONGOING') return 'Approved';
  return 'Pending';
}

/**
 * Maps GET /client/campaign/:id/collaborations payload to ContentReviewSection rows.
 */
export function mapCampaignCollaborationsToCollaborators(
  collaborations: ClientCampaignCollaboration[]
): Collaborator[] {
  return collaborations.map((c) => {
    const inf = c.influencer ?? { handle: '–' };
    const infRec = inf as Record<string, unknown>;
    const influencerIdRaw = infRec.influencer_id ?? infRec.influencerId;
    const influencerId =
      typeof influencerIdRaw === 'string' && influencerIdRaw.trim() ? influencerIdRaw.trim() : undefined;
    const platform = apiPlatformToUi(inf.platform ?? c.platforms?.[0]);
    const products = mergeWithDeepScannedProducts(c, extractCollaborationProducts(c));
    const collabRaw = c as Record<string, unknown>;
    const hub = readOptionalString(collabRaw, 'content_hub_url', 'contentHubUrl');
    const collaborationMediaFallbacks: string[] =
      hub && looksLikeHttpUrl(hub) ? [hub] : [];
    const beats = Array.isArray(c.beats) ? c.beats : [];
    const briefBeatLinks: CollaboratorBriefLink[] = [];
    for (const b of beats) {
      const id = typeof b.beat_id === 'string' ? b.beat_id.trim() : '';
      if (!id) continue;
      const labelRaw =
        typeof b.beat_name === 'string' && b.beat_name.trim() ? b.beat_name.trim() : id;
      briefBeatLinks.push({ beatId: id, label: labelRaw });
    }
    const beatLabel =
      beats.length > 0
        ? beats
            .map((b) => (typeof b.beat_name === 'string' ? b.beat_name : b.beat_id))
            .filter(Boolean)
            .join(', ')
        : '–';
    const fc = inf.followers_count;
    const followers =
      typeof fc === 'number' && Number.isFinite(fc) ? formatFollowers(fc) : '–';
    const er = inf.engagement_rate;
    const engRate =
      typeof er === 'number' && Number.isFinite(er)
        ? `${(er <= 1 ? er * 100 : er).toFixed(1)}%`
        : '–';
    const draftDates = products
      .map((p) => readOptionalString(productRaw(p), 'date_draft', 'dateDraft'))
      .filter((d): d is string => typeof d === 'string' && d.length > 0);
    const publishDates = products
      .map((p) => readOptionalString(productRaw(p), 'date_publish', 'datePublish'))
      .filter((d): d is string => typeof d === 'string' && d.length > 0);
    const deadline =
      draftDates.length > 0 ? formatCollabDate(draftDates.sort().slice(-1)[0]) : '–';
    const publication =
      publishDates.length > 0
        ? formatCollabDate(publishDates.sort().slice(-1)[0])
        : '–';
    const addr =
      typeof c.delivery_address === 'string' && c.delivery_address.trim().length > 0
        ? c.delivery_address.trim()
        : '–';

    const posts = products.map((p) => productToPost(p, platform, collaborationMediaFallbacks));
    const compliance_available = posts.some((p) => p.compliance_from_api === true);
    const trackers = parseCollaborationTrackers(c, products);

    return {
      id: collaborationIdToNumericId(c.collaboration_id),
      collaborationId: typeof c.collaboration_id === 'string' && c.collaboration_id.trim() ? c.collaboration_id : undefined,
      ...(influencerId ? { influencerId } : {}),
      name: typeof inf.name === 'string' && inf.name.trim() ? inf.name : inf.handle,
      handle: inf.handle || '–',
      avatar: typeof inf.mugshot_url === 'string' ? inf.mugshot_url : '',
      beat: beatLabel,
      followers,
      engRate,
      deadline,
      publication,
      status: mapRowStatus(c, products),
      platform,
      address: addr,
      posts,
      product_delivery: c.product_delivery ?? null,
      address_available: addr !== '–',
      compliance_available,
      trackers,
      ...(briefBeatLinks.length > 0 ? { briefBeatLinks } : {}),
    };
  });
}
